import { type ReactNode } from "react";

import { Box } from "ui/box";
import { Select, type FormatOptionLabelMeta } from "ui/select";
import { Switch } from "ui/switch";

import {
  CONTROLS_SX,
  OPTION_LABEL_SX,
  SCREEN_SIZE_SELECT_SX,
} from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.styles";
import {
  SCREEN_SIZE_LABELS,
  SCREEN_SIZES,
  type ScreenSize,
} from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.constants";

import Icons from "assets";

type ScreenSizeOption = { label: string; value: ScreenSize };

type PreviewControlsProps = {
  screenSize: ScreenSize;
  onScreenSizeChange: (screenSize: ScreenSize) => void;
  isBaseFontApplied: boolean;
  onBaseFontChange: (isBaseFontApplied: boolean) => void;
};

const SCREEN_SIZE_ICONS: Record<ScreenSize, ReactNode> = {
  small: <Icons.ScreenSmall />,
  medium: <Icons.ScreenMedium />,
  large: <Icons.ScreenLarge />,
};

const SCREEN_SIZE_OPTIONS: ScreenSizeOption[] = SCREEN_SIZES.map((value) => ({
  value,
  label: SCREEN_SIZE_LABELS[value],
}));

/** The field shows the selected size's icon as its `startIcon`, so only the menu rows draw their own. */
const formatScreenSizeOption = ({ value, label }: ScreenSizeOption, { context }: FormatOptionLabelMeta) =>
  context === "menu" ? (
    <Box component="span" sx={OPTION_LABEL_SX}>
      {SCREEN_SIZE_ICONS[value]}
      {label}
    </Box>
  ) : (
    label
  );

export const PreviewControls = ({
  screenSize,
  onScreenSizeChange,
  isBaseFontApplied,
  onBaseFontChange,
}: PreviewControlsProps) => {
  const handleScreenSizeChange = (option: ScreenSizeOption | null) => {
    if (option) {
      onScreenSizeChange(option.value);
    }
  };

  return (
    <Box sx={CONTROLS_SX}>
      <Select<ScreenSizeOption>
        selectName="theme-preview-screen-size"
        ariaLabel="Screen size"
        size="small"
        options={SCREEN_SIZE_OPTIONS}
        value={SCREEN_SIZE_OPTIONS.find(({ value }) => value === screenSize)}
        onChange={handleScreenSizeChange}
        startIcon={SCREEN_SIZE_ICONS[screenSize]}
        formatOptionLabel={formatScreenSizeOption}
        disableClearable
        isSearchable={false}
        sx={SCREEN_SIZE_SELECT_SX}
      />
      <Switch
        size="small"
        toggleName="Base font style"
        checked={isBaseFontApplied}
        onChange={onBaseFontChange}
      />
    </Box>
  );
};
